import type { ProjectCliLogins } from "@/api/spaces";
import {
  PocketRow,
  PocketSection,
  SelectField,
  type SelectFieldOption,
} from "@/components/ui";
import { personalRuntime } from "@/desktop/onboarding/post-demo-runtime-preference";
import { useT } from "@/i18n";
import { settingsCliLoginsMessages } from "@/i18n/messages/settings-cli-logins";
import { useCompanionStore } from "@/stores/companion-store";
import { claudeLoginOf, DEFAULT_LOGIN } from "./project-claude-login";
import { useCliLogins } from "./use-cli-logins";

interface ClaudeLoginPickerProps {
  cliLogins: ProjectCliLogins | undefined;
  disabled: boolean;
  onChange: (cliLogins: ProjectCliLogins) => void;
}

interface ProjectClaudeLoginSectionProps extends ClaudeLoginPickerProps {
  title: string;
}

/** Desktop app only: which Claude login this project's new chats run on.
 * The whole section stays hidden while Claude Code is not installed. */
export function ProjectClaudeLoginSection({
  title,
  ...props
}: ProjectClaudeLoginSectionProps) {
  const installed = useCompanionStore((state) =>
    Boolean(personalRuntime(state.availableRuntimes, "claude_cli")),
  );
  return installed ? (
    <PocketSection title={title} testId="project-account">
      <ClaudeLoginPicker {...props} />
    </PocketSection>
  ) : null;
}

function ClaudeLoginPicker({
  cliLogins,
  disabled,
  onChange,
}: ClaudeLoginPickerProps) {
  const t = useT(settingsCliLoginsMessages);
  const { logins } = useCliLogins("claude_cli", { withStatus: false });
  const chosen = claudeLoginOf(cliLogins);
  const options: SelectFieldOption[] = [
    { value: DEFAULT_LOGIN, label: t("defaultLogin") },
    ...(logins ?? []).map((login) => ({ value: login.id, label: login.name })),
  ];
  if (
    chosen !== DEFAULT_LOGIN &&
    logins &&
    !logins.some((login) => login.id === chosen)
  )
    options.push({ value: chosen, label: t("notOnThisComputer") });

  function choose(value: string) {
    if (value === chosen) return;
    onChange({ ...cliLogins, claude_cli: value });
  }

  return (
    <PocketRow
      testId="project-claude-login-row"
      title={t("claudeLogin")}
      trailing={
        <SelectField
          variant="value"
          value={chosen}
          options={options}
          onChange={choose}
          // The saved login's name is unknown until the list is read.
          placeholder={logins ? undefined : ""}
          disabled={disabled || logins === null}
          ariaLabel={t("claudeLogin")}
          testId="project-claude-login"
        />
      }
    />
  );
}
